import React, { useRef, useState } from 'react';
import { panelStyle, mutedStyle, fieldStyle, toolPath } from '../tools/toolApi';

function eventId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto?.getRandomValues?.(new Uint8Array(16));
  if (!bytes) throw new Error('A secure request identifier is unavailable. Reload before recording a tool outcome.');
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function Slot({ slot, results, canEdit, disabled, onConfirm }) {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(null);
  const recorded = results.filter(result => result.toolSlotKey === slot.slotKey);
  const release = slot.release || {};
  const releaseId = release.id || release._id;
  const submit = async () => {
    setError('');
    try {
      const payload = { slotKey: slot.slotKey, releaseId, outcome, notes: notes.trim() };
      const signature = JSON.stringify(payload);
      if (pending.current?.signature !== signature) pending.current = { signature, id: eventId() };
      await onConfirm({ ...payload, clientEventId: pending.current.id });
    } catch (failure) { setError(failure.message); }
  };
  return <article style={{ ...panelStyle, marginBottom: '.8rem' }}>
    <h3>{slot.tool?.name || slot.tool?.documentNumber || slot.slotKey}{slot.required !== false ? ' · Required' : ' · Optional'}</h3>
    <p>Exact release: <strong>{release.tag || release.version || 'Unavailable'}</strong> · {slot.tool?.documentNumber}</p>
    <p style={{ ...mutedStyle, overflowWrap: 'anywhere' }}>Commit: <code>{release.commitSha || 'Unavailable'}</code></p>
    <p style={{ ...mutedStyle, overflowWrap: 'anywhere' }}>Manifest SHA256: <code>{release.manifestHash || 'Unavailable'}</code></p>
    <p>Approval: {slot.approval?.documentNumber || slot.approval?.id || 'Unavailable'} · {slot.approval?.status || 'Frozen approval'}</p>
    {releaseId && slot.tool?.documentNumber && <a href={`${toolPath(slot.tool.documentNumber)}?release=${encodeURIComponent(releaseId)}`} target="_blank" rel="noreferrer">Open exact version and downloads</a>}
    {(release.artefacts || []).map(artefact => <p key={artefact.name} style={{ ...mutedStyle, overflowWrap: 'anywhere' }}>{artefact.name} · SHA256 <code>{artefact.sha256}</code></p>)}
    {recorded.map(result => <div key={result.resultId || result.clientEventId} style={{ borderTop: '1px solid var(--ni-border-soft)', paddingTop: '.6rem' }}>
      <strong>Recorded operator attestation: {result.outcome}</strong>
      <p>{result.recordedByName || result.recordedById} · {result.recordedAt ? new Date(result.recordedAt).toLocaleString() : 'Time unavailable'}</p>
      <p style={{ whiteSpace: 'pre-wrap' }}>{result.notes}</p>
    </div>)}
    {canEdit && !recorded.length && slot.mode === 'operator-confirmed' && releaseId && <fieldset disabled={disabled} style={{ border: 0, padding: 0, marginTop: '.7rem' }}>
      <label style={{ display: 'flex', gap: '.5rem', alignItems: 'start' }}><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />
        I used this exact external tool version and checked its outcome for this unit.</label>
      <label style={{ display: 'block', marginTop: '.6rem' }}>Tool outcome — {slot.slotKey}
        <select style={fieldStyle} value={outcome} onChange={event => setOutcome(event.target.value)}>
          <option value="">Select observed outcome</option><option value="pass">Pass</option><option value="fail">Fail</option><option value="hold">Hold</option>
        </select>
      </label>
      <label style={{ display: 'block', marginTop: '.6rem' }}>Tool notes — {slot.slotKey}<textarea rows={2} style={fieldStyle} value={notes} onChange={event => setNotes(event.target.value)} /></label>
      {['fail', 'hold'].includes(outcome) && <p>Recording this outcome locks the station run as failed or held. Explain the reason before recording.</p>}
      <button className="ni-button" type="button" style={{ marginTop: '.6rem' }} disabled={disabled || !confirmed || !outcome || (outcome !== 'pass' && !notes.trim())}
        onClick={submit}>Record tool outcome — {slot.slotKey}</button>
    </fieldset>}
    {slot.mode !== 'operator-confirmed' && <p role="alert">This tool mode cannot be recorded here.</p>}
    {error && <p role="alert">{error}</p>}
  </article>;
}

export default function RequiredSoftwarePanel({ run, canEdit, disabled, onConfirm }) {
  const slots = run.snapshot?.software?.slots || [];
  if (!slots.length) return null;
  return <section style={panelStyle} aria-label="Required software">
    <h2>Required tools and software</h2>
    <p>Site: <strong>{run.site || run.snapshot?.site || 'Unavailable'}</strong></p>
    <p style={mutedStyle}>Use the version frozen into this run. These entries record operator attestations about an external tool; they do not independently prove execution or import machine measurements.</p>
    {slots.map(slot => <Slot key={`${run._id}:${slot.slotKey}:${slot.release?.id || slot.release?._id}`} slot={slot} results={run.toolResults || []}
      canEdit={canEdit} disabled={disabled} onConfirm={onConfirm} />)}
  </section>;
}
